import { useMotionValue } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { rideInvestigationRideIdAtom } from "@ryde/shared";

import {
  PLAYBACK_BASE_WALL_MS,
  PLAYBACK_DEFAULT_SPEED,
} from "../investigation.constants";
import { IPlaybackEvent } from "../investigation.models";
import { eventsInRange, timeForFraction } from "../investigation.utils";
import { useRideInvestigation } from "../use-ride-investigation";
import { IRideInvestigationPlaybackContext } from "./ride-investigation-playback.context";

/**
 * Owns playback state for the investigated ride and drives the playhead with requestAnimationFrame,
 * returning the ready context value. Kept as a hook so the provider component stays a thin wrapper
 * (JSX only) instead of mixing the whole state machine with the render.
 */
export const useRideInvestigationPlaybackValue =
  (): IRideInvestigationPlaybackContext => {
    const rideId = useRecoilValue(rideInvestigationRideIdAtom);
    const setRideId = useSetRecoilState(rideInvestigationRideIdAtom);
    const { model, loading, error } = useRideInvestigation(rideId);

    const playheadMs = useMotionValue(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [speedMultiplier, setSpeedMultiplier] = useState<number>(
      PLAYBACK_DEFAULT_SPEED,
    );
    const [justFiredEvent, setJustFiredEvent] = useState<
      IPlaybackEvent | undefined
    >(undefined);

    const isPlayingRef = useRef(isPlaying);
    isPlayingRef.current = isPlaying;
    const speedRef = useRef(speedMultiplier);
    speedRef.current = speedMultiplier;
    const modelRef = useRef(model);
    modelRef.current = model;

    // Reset the playhead to the window start (and stop) only when a *different* ride is investigated —
    // not when the same ride's trail upgrades (a renderState version bump rebuilds the model).
    const lastRideRef = useRef<number | undefined>(undefined);

    useEffect(() => {
      if (!model) {
        lastRideRef.current = undefined;

        return;
      }

      if (lastRideRef.current === model.payload.summary.rideId) {
        return;
      }

      lastRideRef.current = model.payload.summary.rideId;
      playheadMs.set(model.startMs);
      setIsPlaying(false);
      setJustFiredEvent(undefined);
    }, [model, playheadMs]);

    const fireCrossedEvents = useEvent((fromMs: number, toMs: number) => {
      const activeModel = modelRef.current;

      if (!activeModel) {
        return;
      }

      const crossed = eventsInRange({ model: activeModel, fromMs, toMs });

      if (crossed.length) {
        setJustFiredEvent(crossed[crossed.length - 1]);
      }
    });

    useEffect(() => {
      if (!isPlaying || !model) {
        return undefined;
      }

      let raf = 0;
      let lastTs: number | null = null;

      const step = (ts: number): void => {
        const activeModel = modelRef.current;

        if (!activeModel) {
          return;
        }

        if (lastTs === null) {
          lastTs = ts;
        }

        const deltaWall = ts - lastTs;
        lastTs = ts;

        const rate =
          (activeModel.durationMs / PLAYBACK_BASE_WALL_MS) * speedRef.current;
        const prev = playheadMs.get();
        const next = prev + deltaWall * rate;

        if (next >= activeModel.endMs) {
          playheadMs.set(activeModel.endMs);
          fireCrossedEvents(prev, activeModel.endMs);
          setIsPlaying(false);

          return;
        }

        playheadMs.set(next);
        fireCrossedEvents(prev, next);
        raf = requestAnimationFrame(step);
      };

      raf = requestAnimationFrame(step);

      return () => cancelAnimationFrame(raf);
    }, [isPlaying, model, playheadMs, fireCrossedEvents]);

    const play = useEvent(() => {
      const activeModel = modelRef.current;

      // Without GPS there is no drive to replay; the timeline can still be scrubbed.
      if (!activeModel?.hasGps) {
        return;
      }

      if (playheadMs.get() >= activeModel.endMs - 1) {
        playheadMs.set(activeModel.startMs);
      }

      setIsPlaying(true);
    });

    const pause = useEvent(() => setIsPlaying(false));

    const toggle = useEvent(() => {
      if (isPlayingRef.current) {
        setIsPlaying(false);
      } else {
        play();
      }
    });

    const seekToMs = useEvent((ms: number) => {
      const activeModel = modelRef.current;

      if (!activeModel) {
        return;
      }

      playheadMs.set(
        Math.min(Math.max(ms, activeModel.startMs), activeModel.endMs),
      );
    });

    const seekToFraction = useEvent((fraction: number) => {
      const activeModel = modelRef.current;

      if (activeModel) {
        seekToMs(timeForFraction(activeModel, fraction));
      }
    });

    const restart = useEvent(() => {
      const activeModel = modelRef.current;

      if (!activeModel) {
        return;
      }

      playheadMs.set(activeModel.startMs);
      setIsPlaying(activeModel.hasGps);
    });

    const reset = useEvent(() => {
      const activeModel = modelRef.current;

      if (!activeModel) {
        return;
      }

      setIsPlaying(false);
      playheadMs.set(activeModel.startMs);
      setJustFiredEvent(undefined);
    });

    const setSpeed = useEvent((multiplier: number) =>
      setSpeedMultiplier(multiplier),
    );

    const close = useEvent(() => {
      setIsPlaying(false);
      setRideId(undefined);
    });

    return useMemo<IRideInvestigationPlaybackContext>(
      () => ({
        rideId,
        model,
        loading,
        error,
        isOpen: rideId !== undefined,
        isPlaying,
        speedMultiplier,
        playheadMs,
        justFiredEvent,
        play,
        pause,
        toggle,
        seekToMs,
        seekToFraction,
        setSpeedMultiplier: setSpeed,
        restart,
        reset,
        close,
      }),
      [
        rideId,
        model,
        loading,
        error,
        isPlaying,
        speedMultiplier,
        playheadMs,
        justFiredEvent,
        play,
        pause,
        toggle,
        seekToMs,
        seekToFraction,
        setSpeed,
        restart,
        reset,
        close,
      ],
    );
  };
